import { useState, useEffect, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { DataTableProps, FilterState } from '../../types/data-table';
import type { PaginationMeta } from '../../types/pagination';
import { autoFormatField } from '../../utils/formatters';
import Toggle from '../Toggle';

export default function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  onToggleEnabled,
  onEdit,
  onDelete,
  getId,
  getEnabled = (item) => item.enabled ?? true,
  customFilters,
  emptyMessage = 'Nenhum registro encontrado.',
  actions,
  pagination,
  onPageChange,
  onLimitChange,
  onFiltersChange,
}: DataTableProps<T>) {
  // Validar que paginação está presente
  if (!pagination) {
    console.error('❌ DataTable requer paginação obrigatória');
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800 font-medium">Erro: DataTable requer paginação obrigatória</p>
        <p className="text-red-600 text-sm mt-1">Por favor, forneça as props pagination, onPageChange e onLimitChange</p>
      </div>
    );
  }

  if (!onPageChange || !onLimitChange) {
    console.error('❌ DataTable requer onPageChange e onLimitChange obrigatórios');
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800 font-medium">Erro: DataTable requer onPageChange e onLimitChange obrigatórios</p>
      </div>
    );
  }
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    enabled: 'all',
    search: '',
    dateCreatedFrom: '',
    dateCreatedTo: '',
    dateUpdatedFrom: '',
    dateUpdatedTo: '',
  });
  const [isMobile, setIsMobile] = useState(false);
  const actionsBarRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange]);

  const handleSort = (key: string) => {
    // Ordenação será feita no backend quando necessário
  };

  const handleToggleEnabled = async (item: T) => {
    if (!onToggleEnabled) return;
    const id = getId(item);
    const currentEnabled = getEnabled(item);
    try {
      await onToggleEnabled(id, !currentEnabled);
      setSelectedItem(null);
    } catch (error) {
      console.error('Erro ao alterar status:', error);
    }
  };

  const handleEdit = (item: T) => {
    if (onEdit) {
      onEdit(item);
      setSelectedItem(null);
    }
  };

  const handleDelete = (id: number | string) => {
    if (onDelete) {
      onDelete(id);
      setSelectedItem(null);
    }
  };

  const clearFilters = () => {
    const clearedFilters: FilterState = {
      enabled: 'all',
      search: '',
      dateCreatedFrom: '',
      dateCreatedTo: '',
      dateUpdatedFrom: '',
      dateUpdatedTo: '',
    };
    setFilters(clearedFilters);
    if (onFiltersChange) {
      onFiltersChange(clearedFilters);
    }
  };

  const hasActiveFilters = filters.enabled !== 'all' ||
    filters.search !== '' ||
    filters.dateCreatedFrom !== '' ||
    filters.dateCreatedTo !== '' ||
    filters.dateUpdatedFrom !== '' ||
    filters.dateUpdatedTo !== '';

  const handlePageChange = (newPage: number) => {
    if (onPageChange && pagination) {
      if (newPage >= 1 && newPage <= pagination.totalPages) {
        onPageChange(newPage);
        setSelectedItem(null);
      }
    }
  };

  const handleLimitChange = (newLimit: number) => {
    if (onLimitChange) {
      onLimitChange(newLimit);
      if (onPageChange) {
        onPageChange(1);
      }
      setSelectedItem(null);
    }
  };

  const handleRowClick = (item: T) => {
    if (selectedItem && getId(selectedItem) === getId(item)) {
      setSelectedItem(null);
    } else {
      setSelectedItem(item);
    }
  };

  const hasActions = onEdit || onDelete || onToggleEnabled || actions;

  const getItemName = (item: T): string => {
    if (item.name) return item.name;
    if (item.razao_social) return item.razao_social;
    if (item.nome) return item.nome;
    if (item.nome_fantasia) return item.nome_fantasia;
    return 'o registro selecionado';
  };

  useEffect(() => {
    if (selectedItem && hasActions && actionsBarRef.current) {
      requestAnimationFrame(() => {
        if (actionsBarRef.current) {
          const barRect = actionsBarRef.current.getBoundingClientRect();
          const scrollContainer = tableContainerRef.current?.closest('main');

          if (scrollContainer) {
            const containerRect = scrollContainer.getBoundingClientRect();
            const barTopRelativeToContainer = barRect.top - containerRect.top + scrollContainer.scrollTop;

            if (barRect.top < containerRect.top || barRect.bottom > containerRect.bottom) {
              scrollContainer.scrollTo({
                top: barTopRelativeToContainer - 20,
                behavior: 'smooth',
              });
            }
          } else {
            if (barRect.top < 0 || barRect.bottom > window.innerHeight) {
              actionsBarRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }
          }
        }
      });
    }
  }, [selectedItem, hasActions]);

  return (
    <div className="space-y-4" ref={tableContainerRef}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full"
            />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary flex items-center gap-2 ${hasActiveFilters ? 'bg-primary-50 border-primary-500' : ''}`}
          >
            <FunnelIcon className="w-5 h-5" />
            Filtros
            {hasActiveFilters && (
              <span className="bg-primary-500 text-white text-xs rounded-full px-2 py-0.5">
                {Object.values(filters).filter(v => v !== '' && v !== 'all').length}
              </span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="card">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.enabled}
                  onChange={(e) => setFilters({ ...filters, enabled: e.target.value as any })}
                  className="input-field"
                >
                  <option value="all">Todos</option>
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Cadastro (De)
                </label>
                <input
                  type="date"
                  value={filters.dateCreatedFrom}
                  onChange={(e) => setFilters({ ...filters, dateCreatedFrom: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Cadastro (Até)
                </label>
                <input
                  type="date"
                  value={filters.dateCreatedTo}
                  onChange={(e) => setFilters({ ...filters, dateCreatedTo: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Alteração (De)
                </label>
                <input
                  type="date"
                  value={filters.dateUpdatedFrom}
                  onChange={(e) => setFilters({ ...filters, dateUpdatedFrom: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Alteração (Até)
                </label>
                <input
                  type="date"
                  value={filters.dateUpdatedTo}
                  onChange={(e) => setFilters({ ...filters, dateUpdatedTo: e.target.value })}
                  className="input-field"
                />
              </div>

              {customFilters}
            </div>

            {hasActiveFilters && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="btn-secondary text-sm"
                >
                  Limpar Filtros
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedItem && hasActions && !isMobile && (
        <div
          ref={actionsBarRef}
          className="card sticky top-0 z-10 backdrop-blur-md"
          style={{
            backgroundColor: 'rgba(9, 173, 99, 0.25)',
            borderColor: 'var(--btnBorderColor)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm font-medium" style={{ color: 'var(--h1Color)' }}>
                Ações para {getItemName(selectedItem)}
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                {actions && actions(selectedItem)}
                {onToggleEnabled && (
                  <button
                    onClick={() => handleToggleEnabled(selectedItem)}
                    className="btn-primary text-sm"
                    title={getEnabled(selectedItem) ? 'Desativar' : 'Ativar'}
                  >
                    {getEnabled(selectedItem) ? 'Desativar' : 'Ativar'}
                  </button>
                )}
                {onEdit && (
                  <button
                    onClick={() => handleEdit(selectedItem)}
                    className="btn-primary text-sm"
                    title="Editar"
                  >
                    <PencilIcon className="w-4 h-4 inline mr-1" />
                    Editar
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => handleDelete(getId(selectedItem))}
                    className="text-sm font-medium px-4 py-2 rounded-lg text-white transition-colors duration-200"
                    style={{ backgroundColor: '#dc2626' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                    title="Excluir"
                  >
                    <TrashIcon className="w-4 h-4 inline mr-1" />
                    Excluir
                  </button>
                )}
              </div>
            </div>
            <button
              onClick={() => setSelectedItem(null)}
              className="p-1 rounded hover:opacity-70 transition-opacity"
              style={{ backgroundColor: 'rgba(9, 173, 99, 0.2)' }}
              title="Fechar"
            >
              <XMarkIcon className="w-5 h-5" style={{ color: 'var(--h1Color)' }} />
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 md:p-6 p-0">
        {loading ? (
          <div className="text-center py-8">
            <div className="text-gray-400">Carregando...</div>
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    {onToggleEnabled && (
                      <th className="text-center py-3 px-4 font-medium text-gray-700 w-16">
                        Ativo
                      </th>
                    )}
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className="text-left py-3 px-4 font-medium text-gray-700"
                      >
                        <button
                          onClick={() => column.sortable !== false && handleSort(column.key)}
                          className={`flex items-center gap-1 ${column.sortable !== false ? 'hover:text-primary-500 cursor-pointer' : ''
                            }`}
                          disabled={column.sortable === false}
                        >
                          {column.label}
                          {column.sortable !== false && (
                            <ChevronUpIcon className="w-4 h-4 opacity-50" />
                          )}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((item) => {
                    const id = getId(item);
                    const enabled = getEnabled(item);
                    const isSelected = selectedItem && getId(selectedItem) === id;
                    return (
                      <tr
                        key={id}
                        onClick={() => handleRowClick(item)}
                        className={`
                          border-b border-gray-100 cursor-pointer transition-colors
                          ${!enabled ? 'opacity-60' : ''}
                          ${isSelected ? 'hover:opacity-90' : 'hover:bg-gray-50'}
                        `}
                        style={isSelected ? { backgroundColor: 'rgba(9, 173, 99, 0.15)' } : {}}
                      >
                        {onToggleEnabled && (
                          <td
                            className="py-3 px-4 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Toggle
                              checked={enabled}
                              onChange={() => handleToggleEnabled(item)}
                              color="emerald"
                              size="sm"
                            />
                          </td>
                        )}

                        {columns.map((column) => {
                          const rawValue = (item as any)[column.key];
                          const displayValue = column.render
                            ? column.render(item)
                            : autoFormatField(column.key, rawValue);

                          return (
                            <td key={column.key} className="py-3 px-4 text-gray-900">
                              {displayValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-2 px-0.5 py-0.5">
              {data.map((item) => {
                const id = getId(item);
                const enabled = getEnabled(item);
                return (
                  <div
                    key={id}
                    className={`
                      bg-white border rounded-lg p-3 md:p-4 transition-colors
                      ${!enabled ? 'opacity-60' : ''}
                      border-gray-200 hover:border-gray-300
                    `}
                  >
                    <div className="flex items-start justify-between gap-3">
                      {onToggleEnabled && (
                        <div
                          className="flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Toggle
                            checked={enabled}
                            onChange={() => handleToggleEnabled(item)}
                            color="emerald"
                            size="sm"
                          />
                        </div>
                      )}

                      <div className="flex-1 min-w-0 space-y-2">
                        {columns.map((column, index) => {
                          if (index >= 3) return null;

                          const rawValue = (item as any)[column.key];
                          const displayValue = column.render
                            ? column.render(item)
                            : autoFormatField(column.key, rawValue);

                          return (
                            <div key={column.key} className="flex flex-col">
                              <span className="text-xs font-medium text-gray-500 mb-1">
                                {column.label}
                              </span>
                              <span className="text-sm text-gray-900 truncate">
                                {displayValue}
                              </span>
                            </div>
                          );
                        })}

                        {columns.length > 3 && (
                          <div className="text-xs text-gray-400 pt-1">
                            +{columns.length - 3} {columns.length - 3 === 1 ? 'campo' : 'campos'}...
                          </div>
                        )}
                      </div>
                    </div>

                    {(onEdit || onDelete || actions) && (
                      <div className="mt-3 pt-3 border-t border-gray-200 flex gap-2" onClick={(e) => e.stopPropagation()}>
                        {actions && actions(item)}
                        {onEdit && (
                          <button
                            onClick={() => handleEdit(item)}
                            className="flex-1 btn-primary text-sm py-2"
                          >
                            <PencilIcon className="w-4 h-4 inline mr-1" />
                            Editar
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={() => handleDelete(id)}
                            className="flex-1 text-sm font-medium px-4 py-2 rounded-lg text-white transition-colors"
                            style={{ backgroundColor: '#dc2626' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                          >
                            <TrashIcon className="w-4 h-4 inline mr-1" />
                            Excluir
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {data.length === 0 && (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <MagnifyingGlassIcon className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-gray-500">{emptyMessage}</p>
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <div className="text-sm text-gray-600 text-center md:text-left">
          {pagination.total === 0
            ? '0 registros'
            : `Mostrando ${((pagination.page - 1) * pagination.limit) + 1} a ${Math.min(pagination.page * pagination.limit, pagination.total)} de ${pagination.total} registro(s)`}
        </div>

        <div className="hidden md:flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Itens por página:</label>
            <select
              value={pagination.limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              className="input-field py-1 px-2 text-sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={40}>40</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || loading}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Página anterior"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum: number;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                    className={`px-3 py-1 border rounded-lg text-sm ${pageNum === pagination.page
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'border-gray-300 hover:bg-gray-50'
                      } disabled:opacity-50`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages || loading}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Próxima página"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="md:hidden flex flex-col gap-4">
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || loading}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Página anterior"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum: number;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                    className={`px-3 py-1 border rounded-lg text-sm ${pageNum === pagination.page
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'border-gray-300 hover:bg-gray-50'
                      } disabled:opacity-50`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages || loading}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Próxima página"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center justify-center gap-2">
            <label className="text-sm text-gray-600">Itens por página:</label>
            <select
              value={pagination.limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              className="input-field py-1 px-2 text-sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={40}>40</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

